import { useEffect, useMemo, useState } from "react";
import { useLocale } from "@/i18n/LocaleProvider";
import { withOverrides } from "@/i18n/overrides";
import { useMarket } from "./MarketProvider";
import { v3Dictionaries, type V3Strings } from "./strings";

/**
 * The copy for the active locale, beside the locale itself.
 *
 * `s` is the design's dictionary with this market's overrides folded in —
 * Bassem's ruling that he manages all site text from Admin. Every screen keeps
 * reading `s.hero.title`; what changed is what `s` is built from. See
 * src/i18n/overrides.ts for why it was done here rather than at a thousand
 * call sites.
 */
export function useV3(): { s: V3Strings; locale: "ar" | "en"; ar: boolean; dir: "rtl" | "ltr" } {
  const { locale, ar, dir } = useLocale();
  const { texts } = useMarket();
  const s = useMemo(
    () => withOverrides(v3Dictionaries[locale], "v3", texts[locale]),
    [locale, texts],
  );
  return { s, locale, ar, dir };
}

/* 768px is the design's own breakpoint -- every @media in components.css is
 * (max-width:767px), so this and the stylesheet cannot disagree.
 *
 * WHY A HOOK AND NOT CSS. The two home files are not one layout at two widths:
 * at 1440 the types are an eight-column grid of tiles, at 390 they are a
 * scrolling chip row; the search bar is four cells in a row or three stacked
 * boxes; the header carries a nav or a hamburger. Those are different
 * elements, not different sizes, and rendering both and hiding one would ship
 * two headers to every screen reader and double the markup for no gain. */
export const MOBILE_MAX = 767;

export function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() => {
    try { return window.matchMedia(`(max-width:${MOBILE_MAX}px)`).matches; } catch { return false; }
  });
  useEffect(() => {
    let mq: MediaQueryList;
    try { mq = window.matchMedia(`(max-width:${MOBILE_MAX}px)`); } catch { return; }
    const on = () => setMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return mobile;
}
